import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Calendar as CalendarIcon, Clock, User, Phone, Mail, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { API } from '../App';
import axios from 'axios';
import { format, addDays, isBefore, startOfDay, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay } from 'date-fns';

const LOGO = "https://customer-assets.emergentagent.com/job_logan-salon/artifacts/cyvqt31h_IMG_6012.jpg";

export default function BookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [blockedDates, setBlockedDates] = useState([]);
  
  const [booking, setBooking] = useState({
    service: null,
    date: null,
    time: null,
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    notes: '',
    discountCode: ''
  });

  useEffect(() => {
    fetchServices();
    fetchBlockedDates();
  }, []);

  // Polling: Refresh available slots every 15 seconds when on step 2 and date is selected
  useEffect(() => {
    if (step !== 2 || !booking.date) return;
    
    const intervalId = setInterval(() => {
      fetchAvailableSlots(booking.date, true); // silent refresh
    }, 15000);
    
    return () => clearInterval(intervalId);
  }, [step, booking.date]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const response = await axios.get(`${API}/blocked-dates`);
      // Only store dates that are FULLY blocked (time is null)
      const fullDayBlocks = response.data.filter(b => !b.time).map(b => b.date);
      setBlockedDates(fullDayBlocks);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const fetchAvailableSlots = async (date, silent = false) => {
    if (!silent) setSlotsLoading(true);
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/available-slots/${formattedDate}`);
      
      if (!response.data.open) {
        setAvailableSlots([]);
        if (!silent) toast.info('Shop is closed on this day. Please select another date.');
      } else {
        // Check if user's selected time became unavailable
        if (booking.time && silent) {
          const selectedSlot = response.data.slots.find(s => s.time === booking.time);
          if (selectedSlot && !selectedSlot.available) {
            toast.warning('Your selected time is no longer available. Please choose another.');
            setBooking(prev => ({ ...prev, time: null }));
          }
        }
        setAvailableSlots(response.data.slots);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      if (!silent) toast.error('Failed to load available times');
    } finally {
      if (!silent) setSlotsLoading(false);
    }
  };

  const handleServiceSelect = (service) => {
    setBooking({ ...booking, service });
    setStep(2);
  };

  const handleDateSelect = (date) => {
    setBooking({ ...booking, date, time: null });
    fetchAvailableSlots(date);
  };

  const handleTimeSelect = (time) => {
    setBooking({ ...booking, time });
    setStep(3);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBooking({ ...booking, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!booking.clientName || !booking.clientPhone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const bookingData = {
        service_id: booking.service.id,
        date: format(booking.date, 'yyyy-MM-dd'),
        time: booking.time,
        client_name: booking.clientName,
        client_phone: booking.clientPhone,
        client_email: booking.clientEmail || null,
        notes: booking.notes || null,
        discount_code: booking.discountCode || null
      };

      await axios.post(`${API}/bookings`, bookingData);
      toast.success('Booking confirmed! We look forward to seeing you.');
      setStep(4);
    } catch (error) {
      console.error('Error creating booking:', error);
      const message = error.response?.data?.detail || 'Failed to create booking. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Group services by category
  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {});

  // Check if date is disabled
  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    if (isBefore(date, today)) return true;
    const dayOfWeek = date.getDay();
    if ([0, 1, 2, 6].includes(dayOfWeek)) return true;
    // Check if date is blocked
    const dateStr = format(date, 'yyyy-MM-dd');
    if (blockedDates.includes(dateStr)) return true;
    return false;
  };

  // Check if date is blocked
  const isDateBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDates.includes(dateStr);
  };

  // Check if date is a working day
  const isWorkingDay = (date) => {
    const day = getDay(date);
    return day === 3 || day === 4 || day === 5;
  };

  const calculateFinalPrice = () => {
    if (!booking.service) return 0;
    let price = booking.service.price;
    if (booking.discountCode?.toUpperCase() === 'WELCOME5') {
      price = price * 0.95;
    }
    return price.toFixed(2);
  };

  // Calendar helpers
  const calendarDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });
  const startDayOfWeek = getDay(startOfMonth(currentMonth));
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className="min-h-screen bg-cream">
      {/* Header - Mobile optimized */}
      <header className="bg-cream border-b border-[#D6CFC2] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="booking-logo">
            <img src={LOGO} alt="Logan Collective" className="w-8 h-8 rounded-full" />
            <span className="font-heading text-sm sm:text-lg text-[#1A1A1A]">Logan Collective</span>
          </Link>
          <Link to="/" className="flex items-center gap-1 text-[#1A1A1A]/60 hover:text-[#1A1A1A] transition-colors" data-testid="back-home">
            <ArrowLeft size={16} />
            <span className="text-xs sm:text-sm">Back</span>
          </Link>
        </div>
      </header>

      {/* Progress Steps - Compact for mobile */}
      <div className="max-w-4xl mx-auto px-4 py-4 sm:py-8">
        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-6 sm:mb-12">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-all duration-300 ${
                step >= s 
                  ? 'bg-bronze text-cream' 
                  : 'bg-cream-dark text-[#1A1A1A]/40 border border-[#D6CFC2]'
              }`}>
                {step > s ? <Check size={14} /> : s}
              </div>
              {s < 4 && (
                <div className={`w-8 sm:w-16 h-0.5 mx-1 sm:mx-2 transition-all duration-300 ${
                  step > s ? 'bg-bronze' : 'bg-[#D6CFC2]'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Select Service - Mobile optimized with dividers */}
        {step === 1 && (
          <div className="animate-fade-in" data-testid="step-1-service">
            <div className="text-center mb-6 sm:mb-12">
              <h1 className="font-heading text-2xl sm:text-4xl text-[#1A1A1A] mb-1 sm:mb-2">Select a Service</h1>
              <p className="text-[#1A1A1A]/60 text-sm">Choose the service you'd like to book</p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="space-y-8 sm:space-y-10">
                {Object.entries(groupedServices).map(([category, categoryServices], idx) => (
                  <div key={category}>
                    {/* Category Header with bronze divider */}
                    <div className="mb-4 sm:mb-6">
                      <h3 className="font-heading text-lg sm:text-xl text-[#1A1A1A] mb-2">{category}</h3>
                      <div className="h-0.5 bg-bronze/40 w-full"></div>
                    </div>
                    <div className="space-y-3">
                      {categoryServices.map((service) => (
                        <button
                          key={service.id}
                          onClick={() => handleServiceSelect(service)}
                          className="service-card w-full flex items-center justify-between p-4 sm:p-5 bg-cream-dark border border-[#D6CFC2] hover:border-bronze text-left transition-all duration-300"
                          data-testid={`select-service-${service.id}`}
                        >
                          <div className="flex-1 min-w-0 pr-3">
                            <h4 className="font-medium text-sm sm:text-base text-[#1A1A1A] mb-0.5">{service.name}</h4>
                            <p className="text-xs sm:text-sm text-[#1A1A1A]/60 line-clamp-1">{service.description}</p>
                            <p className="text-xs text-bronze mt-1">{service.duration} min</p>
                          </div>
                          <span className="font-heading text-xl sm:text-2xl text-bronze flex-shrink-0">${service.price}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Date & Time - Mobile optimized calendar */}
        {step === 2 && (
          <div className="animate-fade-in" data-testid="step-2-datetime">
            <div className="text-center mb-6 sm:mb-12">
              <h1 className="font-heading text-2xl sm:text-4xl text-[#1A1A1A] mb-1 sm:mb-2">Select Date & Time</h1>
              <p className="text-[#1A1A1A]/60 text-sm">Choose when you'd like to visit</p>
            </div>

            {/* Custom Calendar - Mobile friendly, smaller on desktop */}
            <div className="bg-cream-dark border border-[#D6CFC2] p-4 sm:p-5 mb-4 max-w-md mx-auto">
              <div className="flex items-center justify-between mb-4">
                <button 
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-2 sm:p-3 hover:bg-[#D6CFC2] transition-colors rounded-full"
                >
                  <ChevronLeft size={20} className="text-bronze" />
                </button>
                <h3 className="font-heading text-lg sm:text-xl text-[#1A1A1A]">
                  {format(currentMonth, 'MMMM yyyy')}
                </h3>
                <button 
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-2 sm:p-3 hover:bg-[#D6CFC2] transition-colors rounded-full"
                >
                  <ChevronRight size={20} className="text-bronze" />
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day, i) => (
                  <div key={i} className="text-center py-1.5 text-xs sm:text-sm font-medium text-[#1A1A1A]/50">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {[...Array(startDayOfWeek)].map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}
                {calendarDays.map(day => {
                  const isSelected = booking.date && isSameDay(day, booking.date);
                  const isDisabled = isDateDisabled(day);
                  const isWorking = isWorkingDay(day);
                  const blocked = isDateBlocked(day);

                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => !isDisabled && handleDateSelect(day)}
                      disabled={isDisabled}
                      className={`aspect-square flex items-center justify-center text-sm sm:text-base font-medium rounded-lg transition-all ${
                        isSelected 
                          ? 'bg-bronze text-cream' 
                          : blocked
                            ? 'bg-red-100 text-red-400 cursor-not-allowed'
                            : isDisabled
                              ? 'text-[#1A1A1A]/20 cursor-not-allowed'
                              : isWorking
                                ? 'bg-cream hover:bg-bronze/20 text-[#1A1A1A]'
                                : 'text-[#1A1A1A]/30'
                      }`}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-[#1A1A1A]/50 mt-4 text-center">
                Open: Wed, Thu, Fri only
              </p>
            </div>

            {/* Time Slots */}
            <div className="bg-cream-dark border border-[#D6CFC2] p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock size={18} className="text-bronze" />
                <h3 className="font-heading text-lg sm:text-xl text-[#1A1A1A]">Select Time</h3>
              </div>
              
              {!booking.date ? (
                <p className="text-[#1A1A1A]/50 text-center py-6 text-sm">Please select a date first</p>
              ) : slotsLoading ? (
                <div className="flex justify-center py-6">
                  <div className="spinner"></div>
                </div>
              ) : availableSlots.length === 0 ? (
                <p className="text-[#1A1A1A]/50 text-center py-6 text-sm">No available slots</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.time}
                      onClick={() => slot.available && handleTimeSelect(slot.time)}
                      disabled={!slot.available}
                      className={`py-3 px-2 text-sm sm:text-base font-medium rounded-lg border transition-all duration-300 ${
                        booking.time === slot.time
                          ? 'bg-bronze text-cream border-bronze'
                          : slot.available
                            ? 'bg-cream border-[#D6CFC2] hover:border-bronze'
                            : slot.blocked
                              ? 'bg-red-50 text-red-300 border-red-200 cursor-not-allowed'
                              : 'bg-cream/50 text-[#1A1A1A]/30 border-[#D6CFC2]/50 cursor-not-allowed line-through'
                      }`}
                      data-testid={`time-slot-${slot.time.replace(':', '')}`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Service Summary */}
            {booking.service && (
              <div className="mt-4 p-3 sm:p-4 bg-[#1A1A1A] text-cream flex items-center justify-between">
                <div>
                  <p className="text-cream/60 text-xs">Selected</p>
                  <p className="font-medium text-sm">{booking.service.name}</p>
                </div>
                <span className="font-heading text-lg text-bronze">${booking.service.price}</span>
              </div>
            )}

            <div className="mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-cream px-6 py-3 text-sm"
                data-testid="back-to-step-1"
              >
                ← Back
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Client Details - Mobile optimized */}
        {step === 3 && (
          <div className="animate-fade-in" data-testid="step-3-details">
            <div className="text-center mb-6 sm:mb-12">
              <h1 className="font-heading text-2xl sm:text-4xl text-[#1A1A1A] mb-1 sm:mb-2">Your Details</h1>
              <p className="text-[#1A1A1A]/60 text-sm">Let us know how to reach you</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 max-w-lg mx-auto">
              <div>
                <Label htmlFor="clientName" className="flex items-center gap-2 mb-2 text-[#1A1A1A]/80 text-sm">
                  <User size={14} />
                  Name *
                </Label>
                <Input
                  id="clientName"
                  name="clientName"
                  value={booking.clientName}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                  required
                  className="bg-cream border border-[#D6CFC2] rounded-lg px-4 py-3 text-base focus:ring-1 focus:ring-bronze focus:border-bronze"
                  data-testid="input-name"
                />
              </div>

              <div>
                <Label htmlFor="clientPhone" className="flex items-center gap-2 mb-2 text-[#1A1A1A]/80 text-sm">
                  <Phone size={14} />
                  Phone *
                </Label>
                <Input
                  id="clientPhone"
                  name="clientPhone"
                  type="tel"
                  value={booking.clientPhone}
                  onChange={handleInputChange}
                  placeholder="Your phone number"
                  required
                  className="bg-cream border border-[#D6CFC2] rounded-lg px-4 py-3 text-base focus:ring-1 focus:ring-bronze focus:border-bronze"
                  data-testid="input-phone"
                />
              </div>

              <div>
                <Label htmlFor="clientEmail" className="flex items-center gap-2 mb-2 text-[#1A1A1A]/80 text-sm">
                  <Mail size={14} />
                  Email (optional)
                </Label>
                <Input
                  id="clientEmail"
                  name="clientEmail"
                  type="email"
                  value={booking.clientEmail}
                  onChange={handleInputChange}
                  placeholder="Your email address"
                  className="bg-cream border border-[#D6CFC2] rounded-lg px-4 py-3 text-base focus:ring-1 focus:ring-bronze focus:border-bronze"
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label htmlFor="discountCode" className="flex items-center gap-2 mb-2 text-[#1A1A1A]/80 text-sm">
                  Discount Code
                </Label>
                <Input
                  id="discountCode"
                  name="discountCode"
                  value={booking.discountCode}
                  onChange={handleInputChange}
                  placeholder=""
                  className="bg-cream border border-[#D6CFC2] rounded-lg px-4 py-3 text-base focus:ring-1 focus:ring-bronze focus:border-bronze"
                  data-testid="input-discount"
                />
              </div>

              {/* Booking Summary - Compact */}
              <div className="p-4 bg-cream-dark border border-[#D6CFC2] rounded-lg">
                <h3 className="font-heading text-lg text-[#1A1A1A] mb-3">Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#1A1A1A]/60">Service</span>
                    <span className="font-medium text-right">{booking.service?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#1A1A1A]/60">When</span>
                    <span className="font-medium text-right">{booking.date ? format(booking.date, 'EEE, d MMM') : '-'} at {booking.time || '-'}</span>
                  </div>
                  {booking.discountCode?.toUpperCase() === 'WELCOME5' && (
                    <div className="flex justify-between text-green-700">
                      <span>Discount</span>
                      <span>-5%</span>
                    </div>
                  )}
                  <div className="border-t border-[#D6CFC2] pt-2 mt-2 flex justify-between">
                    <span className="font-medium">Total</span>
                    <span className="font-heading text-xl text-bronze">${calculateFinalPrice()}</span>
                  </div>
                </div>
              </div>

              {/* Cancellation Policy */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">Note:</span> 50% fee for cancellations within 24 hours.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-cream px-4 py-3 text-sm"
                  data-testid="back-to-step-2"
                >
                  ← Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-bronze text-cream hover:bg-bronze-light transition-all duration-500 py-3 text-sm tracking-widest uppercase font-bold rounded-lg"
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="animate-fade-in text-center" data-testid="step-4-confirmation">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check size={32} className="text-green-600" />
            </div>
            
            <h1 className="font-heading text-2xl sm:text-4xl text-[#1A1A1A] mb-2">Booking Confirmed!</h1>
            <p className="text-[#1A1A1A]/60 text-sm mb-6">
              We look forward to seeing you!
            </p>

            <div className="bg-cream-dark border border-[#D6CFC2] p-4 sm:p-6 mb-6 text-left rounded-lg max-w-md mx-auto">
              <h3 className="font-heading text-lg text-[#1A1A1A] mb-4 text-center">Your Appointment</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between pb-2 border-b border-[#D6CFC2]">
                  <span className="text-[#1A1A1A]/60">Service</span>
                  <span className="font-medium">{booking.service?.name}</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-[#D6CFC2]">
                  <span className="text-[#1A1A1A]/60">Date</span>
                  <span className="font-medium">{booking.date ? format(booking.date, 'EEE, d MMM yyyy') : '-'}</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-[#D6CFC2]">
                  <span className="text-[#1A1A1A]/60">Time</span>
                  <span className="font-medium">{booking.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1A1A1A]/60">Location</span>
                  <span className="font-medium text-right">364 Middleton Loop<br/>Albany, WA</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 max-w-md mx-auto">
              <Link to="/" className="block">
                <Button className="w-full bg-bronze text-cream hover:bg-bronze-light transition-all duration-500 py-4 text-sm tracking-widest uppercase font-bold rounded-lg" data-testid="back-home-btn">
                  Back to Home
                </Button>
              </Link>
              <a href="tel:0438814493" className="block">
                <Button variant="outline" className="w-full border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-cream py-4 text-sm tracking-widest uppercase font-semibold rounded-lg" data-testid="call-btn">
                  Call Us
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
